import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RichTextEditor } from "@/components/editor/RichTextEditor";
import { notificationVariables } from "@/lib/notificationVariables";
import NotificationImageUpload from "@/components/admin/NotificationImageUpload";
import { toast } from "@/hooks/use-toast";
import { Send, Loader2, Save } from "lucide-react";

export type AudienceTarget = "all" | "free" | "base" | "pro" | "plus";

export interface AdminNotification {
  id: string;
  title_it: string;
  title_en: string | null;
  title_es: string | null;
  description_it: string | null;
  description_en: string | null;
  description_es: string | null;
  image_url: string | null;
  is_evergreen: boolean;
  is_active: boolean;
  send_email?: boolean;
  created_at: string;
  created_by: string | null;
  cta_url?: string | null;
  cta_text_it?: string | null;
  cta_text_en?: string | null;
  cta_text_es?: string | null;
  target_audience?: AudienceTarget[];
}

interface NotificationFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  notification?: AdminNotification | null;
}

export default function NotificationFormModal({
  isOpen,
  onClose,
  notification,
}: NotificationFormModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const isEditMode = !!notification;

  // Form state
  const [titleIt, setTitleIt] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [titleEs, setTitleEs] = useState("");
  const [descriptionIt, setDescriptionIt] = useState("");
  const [descriptionEn, setDescriptionEn] = useState("");
  const [descriptionEs, setDescriptionEs] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [isEvergreen, setIsEvergreen] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [activeTab, setActiveTab] = useState("it");
  // CTA state
  const [ctaUrl, setCtaUrl] = useState("");
  const [ctaTextIt, setCtaTextIt] = useState("");
  const [ctaTextEn, setCtaTextEn] = useState("");
  const [ctaTextEs, setCtaTextEs] = useState("");
  
  // Audience targeting state
  const [targetAudience, setTargetAudience] = useState<AudienceTarget[]>(["all"]);

  const audienceOptions: { value: AudienceTarget; label: string }[] = [
    { value: "all", label: t("admin.notifications.audienceAll", "Tutti gli utenti") },
    { value: "free", label: t("admin.notifications.audienceFree", "Utenti gratuiti") },
    { value: "base", label: t("admin.notifications.audienceBase", "Abbonati Base") },
    { value: "pro", label: t("admin.notifications.audiencePro", "Abbonati Pro") },
    { value: "plus", label: t("admin.notifications.audiencePlus", "Abbonati Plus") },
  ];

  const handleAudienceChange = (value: AudienceTarget, checked: boolean) => {
    if (value === "all") {
      // If selecting "all", deselect everything else
      setTargetAudience(checked ? ["all"] : []);
    } else {
      // If selecting specific audience, remove "all"
      const newAudience = checked
        ? [...targetAudience.filter((a) => a !== "all"), value]
        : targetAudience.filter((a) => a !== value);
      setTargetAudience(newAudience.length > 0 ? newAudience : ["all"]);
    }
  };

  // Populate form when editing
  useEffect(() => {
    if (notification) {
      setTitleIt(notification.title_it || "");
      setTitleEn(notification.title_en || "");
      setTitleEs(notification.title_es || "");
      setDescriptionIt(notification.description_it || "");
      setDescriptionEn(notification.description_en || "");
      setDescriptionEs(notification.description_es || "");
      setImageUrl(notification.image_url || "");
      setIsEvergreen(notification.is_evergreen || false);
      setSendEmail(notification.send_email || false);
      setCtaUrl(notification.cta_url || "");
      setCtaTextIt(notification.cta_text_it || "");
      setCtaTextEn(notification.cta_text_en || "");
      setCtaTextEs(notification.cta_text_es || "");
      setTargetAudience((notification.target_audience as AudienceTarget[]) || ["all"]);
    } else {
      resetForm();
    }
  }, [notification, isOpen]);

  const resetForm = () => {
    setTitleIt("");
    setTitleEn("");
    setTitleEs("");
    setDescriptionIt("");
    setDescriptionEn("");
    setDescriptionEs("");
    setImageUrl("");
    setIsEvergreen(false);
    setSendEmail(false);
    setActiveTab("it");
    setCtaUrl("");
    setCtaTextIt("");
    setCtaTextEn("");
    setCtaTextEs("");
    setTargetAudience(["all"]);
  };

  const createNotificationMutation = useMutation({
    mutationFn: async () => {
      // First, create the notification
      const { data: newNotification, error: notificationError } = await supabase
        .from("admin_notifications")
        .insert({
          title_it: titleIt,
          title_en: titleEn || null,
          title_es: titleEs || null,
          description_it: descriptionIt || null,
          description_en: descriptionEn || null,
          description_es: descriptionEs || null,
          image_url: imageUrl || null,
          is_evergreen: isEvergreen,
          send_email: sendEmail,
          is_active: true,
          created_by: user?.id,
          cta_url: ctaUrl || null,
          cta_text_it: ctaTextIt || null,
          cta_text_en: ctaTextEn || null,
          cta_text_es: ctaTextEs || null,
          target_audience: targetAudience,
        })
        .select()
        .single();

      if (notificationError) throw notificationError;

      // Then, broadcast it to all existing users
      const { error: broadcastError } = await supabase.functions.invoke(
        "broadcast-notification",
        {
          body: { notification_id: newNotification.id },
        }
      );

      if (broadcastError) {
        console.error("Error broadcasting notification:", broadcastError);
        // Don't throw - notification was created, just broadcast failed
      }

      return newNotification;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-notifications"] });
      toast.success(t("admin.notifications.sent", "Notifica inviata con successo!"));
      resetForm();
      onClose();
    },
    onError: (error) => {
      console.error("Error creating notification:", error);
      toast.error(t("common.error"));
    },
  });

  const updateNotificationMutation = useMutation({
    mutationFn: async () => {
      if (!notification) throw new Error("No notification to update");

      const { error } = await supabase
        .from("admin_notifications")
        .update({
          title_it: titleIt,
          title_en: titleEn || null,
          title_es: titleEs || null,
          description_it: descriptionIt || null,
          description_en: descriptionEn || null,
          description_es: descriptionEs || null,
          image_url: imageUrl || null,
          is_evergreen: isEvergreen,
          send_email: sendEmail,
          cta_url: ctaUrl || null,
          cta_text_it: ctaTextIt || null,
          cta_text_en: ctaTextEn || null,
          cta_text_es: ctaTextEs || null,
          target_audience: targetAudience,
          updated_at: new Date().toISOString(),
        })
        .eq("id", notification.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-notifications"] });
      toast.success(t("admin.notifications.updated", "Notifica aggiornata con successo!"));
      onClose();
    },
    onError: (error) => {
      console.error("Error updating notification:", error);
      toast.error(t("common.error"));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!titleIt.trim()) {
      toast.error(t("admin.notifications.titleRequired", "Titolo italiano obbligatorio"));
      return;
    }

    if (isEditMode) {
      updateNotificationMutation.mutate();
    } else {
      createNotificationMutation.mutate();
    }
  };

  const isPending = createNotificationMutation.isPending || updateNotificationMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto border-[3px] border-dark shadow-[8px_8px_0px_0px] shadow-dark">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {isEditMode
              ? t("admin.notifications.edit", "Modifica Notifica")
              : t("admin.notifications.createNew", "Crea Notifica")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Image Upload */}
          <div>
            <Label className="mb-2 block">
              {t("admin.notifications.imageLabel", "Immagine (16:9, opzionale)")}
            </Label>
            <NotificationImageUpload
              imageUrl={imageUrl}
              onImageChange={setImageUrl}
            />
          </div>

          {/* Language Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="it">
                🇮🇹 Italiano {!titleIt && "*"}
              </TabsTrigger>
              <TabsTrigger value="en">🇬🇧 English</TabsTrigger>
              <TabsTrigger value="es">🇪🇸 Español</TabsTrigger>
            </TabsList>

            {/* Italian */}
            <TabsContent value="it" className="space-y-4 mt-4">
              <div>
                <Label htmlFor="title-it">
                  {t("admin.notifications.titleLabel", "Titolo")} *
                </Label>
                <BrutalInput
                  id="title-it"
                  value={titleIt}
                  onChange={(e) => setTitleIt(e.target.value)}
                  placeholder={t("admin.notifications.titlePlaceholder", "Titolo della notifica...")}
                  required
                />
              </div>
              <div>
                <Label>
                  {t("admin.notifications.descriptionLabel", "Descrizione")}
                </Label>
                <RichTextEditor
                  value={descriptionIt}
                  onChange={(html) => setDescriptionIt(html)}
                  placeholder={t("admin.notifications.descriptionPlaceholder", "Descrizione della notifica...")}
                  customVariables={notificationVariables}
                />
              </div>
              <div>
                <Label htmlFor="cta-text-it">
                  {t("admin.notifications.ctaTextLabel", "Testo pulsante CTA")}
                </Label>
                <BrutalInput
                  id="cta-text-it"
                  value={ctaTextIt}
                  onChange={(e) => setCtaTextIt(e.target.value)}
                  placeholder="Scopri di più"
                />
              </div>
            </TabsContent>

            {/* English */}
            <TabsContent value="en" className="space-y-4 mt-4">
              <div>
                <Label htmlFor="title-en">
                  {t("admin.notifications.titleLabel", "Titolo")}
                </Label>
                <BrutalInput
                  id="title-en"
                  value={titleEn}
                  onChange={(e) => setTitleEn(e.target.value)}
                  placeholder="Notification title..."
                />
              </div>
              <div>
                <Label>
                  {t("admin.notifications.descriptionLabel", "Descrizione")}
                </Label>
                <RichTextEditor
                  value={descriptionEn}
                  onChange={(html) => setDescriptionEn(html)}
                  placeholder="Notification description..."
                  customVariables={notificationVariables}
                />
              </div>
              <div>
                <Label htmlFor="cta-text-en">
                  {t("admin.notifications.ctaTextLabel", "Testo pulsante CTA")}
                </Label>
                <BrutalInput
                  id="cta-text-en"
                  value={ctaTextEn}
                  onChange={(e) => setCtaTextEn(e.target.value)}
                  placeholder="Learn more"
                />
              </div>
            </TabsContent>

            {/* Spanish */}
            <TabsContent value="es" className="space-y-4 mt-4">
              <div>
                <Label htmlFor="title-es">
                  {t("admin.notifications.titleLabel", "Titolo")}
                </Label>
                <BrutalInput
                  id="title-es"
                  value={titleEs}
                  onChange={(e) => setTitleEs(e.target.value)}
                  placeholder="Título de la notificación..."
                />
              </div>
              <div>
                <Label>
                  {t("admin.notifications.descriptionLabel", "Descrizione")}
                </Label>
                <RichTextEditor
                  value={descriptionEs}
                  onChange={(html) => setDescriptionEs(html)}
                  placeholder="Descripción de la notificación..."
                  customVariables={notificationVariables}
                />
              </div>
              <div>
                <Label htmlFor="cta-text-es">
                  {t("admin.notifications.ctaTextLabel", "Testo pulsante CTA")}
                </Label>
                <BrutalInput
                  id="cta-text-es"
                  value={ctaTextEs}
                  onChange={(e) => setCtaTextEs(e.target.value)}
                  placeholder="Saber más"
                />
              </div>
            </TabsContent>
          </Tabs>

          {/* CTA URL */}
          <div>
            <Label htmlFor="cta-url">
              {t("admin.notifications.ctaUrlLabel", "Link CTA (opzionale)")}
            </Label>
            <BrutalInput
              id="cta-url"
              type="url"
              value={ctaUrl}
              onChange={(e) => setCtaUrl(e.target.value)}
              placeholder="https://..."
            />
            <p className="text-sm text-muted-foreground mt-1">
              {t("admin.notifications.ctaUrlHelp", "Se valorizzato, mostrerà un pulsante sotto la descrizione")}
            </p>
          </div>

          {/* Target Audience Selector */}
          <div className="p-4 bg-surface rounded-lg border-2 border-dark">
            <Label className="font-bold block mb-3">
              🎯 {t("admin.notifications.targetAudience", "Destinatari")}
            </Label>
            <p className="text-sm text-muted-foreground mb-4">
              {t("admin.notifications.targetAudienceHelp", "Seleziona a quali utenti inviare questa notifica")}
            </p>
            <div className="grid gap-3">
              {audienceOptions.map((option) => (
                <div key={option.value} className="flex items-center gap-3">
                  <Checkbox
                    id={`audience-${option.value}`}
                    checked={targetAudience.includes(option.value)}
                    onCheckedChange={(checked) =>
                      handleAudienceChange(option.value, checked as boolean)
                    }
                    disabled={
                      option.value !== "all" && targetAudience.includes("all")
                    }
                  />
                  <Label
                    htmlFor={`audience-${option.value}`}
                    className={`cursor-pointer ${
                      option.value !== "all" && targetAudience.includes("all")
                        ? "text-muted-foreground"
                        : ""
                    }`}
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Evergreen Toggle */}
          <div className="flex items-center justify-between p-4 bg-surface rounded-lg border-2 border-dark">
            <div>
              <Label htmlFor="evergreen" className="font-bold">
                {t("admin.notifications.evergreen", "Notifica Evergreen")}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t(
                  "admin.notifications.evergreenHelp",
                  "Mostra questa notifica anche ai nuovi utenti che si iscriveranno"
                )}
              </p>
            </div>
            <Switch
              id="evergreen"
              checked={isEvergreen}
              onCheckedChange={setIsEvergreen}
            />
          </div>

          {/* Send Email Toggle - only show for new notifications */}
          {!isEditMode && (
            <div className="flex items-center justify-between p-4 bg-surface rounded-lg border-2 border-dark">
              <div>
                <Label htmlFor="send-email" className="font-bold">
                  📧 {t("admin.notifications.sendEmail", "Invia anche via Email")}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "admin.notifications.sendEmailHelp",
                    "Invia questa notifica anche via email a tutti gli utenti"
                  )}
                </p>
              </div>
              <Switch
                id="send-email"
                checked={sendEmail}
                onCheckedChange={setSendEmail}
              />
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <BrutalButton
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isPending}
            >
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton
              type="submit"
              disabled={isPending || !titleIt.trim()}
            >
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("common.submitting")}
                </>
              ) : isEditMode ? (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {t("common.save", "Salva")}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {t("admin.notifications.send", "Invia Notifica")}
                </>
              )}
            </BrutalButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
